<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Badge, Button, EmptyState, FormField, Input, Modal, Section, Select, Spinner, Table, useToast } from '@drama/ui'
import { listNovelDramaAccounts, saveNovelDramaAccount } from '../../api/novel/drama.js'
import { useNovelPageText } from './novel-page-text.js'

const { t, te } = useI18n()
const toast = useToast()
const { apiErrorLine, dateTime } = useNovelPageText()
const tr = (key, params) => t(`novelDramaAdmin.${key}`, params ?? {})
const items = ref([])
const loading = ref(false)
const error = ref('')
const action_error = ref('')
const fetched_at = ref('')
const busy = ref('')
const editing = ref(null)
const max_sessions = ref('')
const weight = ref('')
const enabled = ref('enabled')
let refresh_timer = null
let disposed = false
let current_load = null

const enabled_options = computed(() => ['enabled', 'disabled'].map(value => ({ value, label: tr(`accounts.${value}`) })))
const parsed_max_sessions = computed(() => /^[1-9]\d*$/.test(max_sessions.value.trim()) ? Number(max_sessions.value) : null)
const max_sessions_invalid = computed(() => !Number.isSafeInteger(parsed_max_sessions.value) || parsed_max_sessions.value > 1000)
const parsed_weight = computed(() => /^[1-9]\d*$/.test(weight.value.trim()) ? Number(weight.value) : null)
const weight_invalid = computed(() => !Number.isSafeInteger(parsed_weight.value) || parsed_weight.value > 1000)
const provider_label = value => te(`novelDramaAdmin.providers.${value}`) ? tr(`providers.${value}`) : value
const quota_label = value => te(`novelDramaAdmin.accounts.quota.${value}`) ? tr(`accounts.quota.${value}`) : (value || tr('accounts.quota.unknown'))
const quota_variant = value => ({ available: 'success', insufficient: 'danger', inactive: 'warning' })[value] || 'neutral'
const timestamp = value => value ? dateTime(value) : tr('common.none')
const has_uncertain = computed(() => items.value.some(account => account.uncertain > 0))

const columns = computed(() => [
  { key: 'account', label: tr('accounts.table.account'), width: '180px', wrap: true },
  { key: 'phases', label: tr('accounts.table.phases'), width: '300px', wrap: true },
  { key: 'capacity', label: tr('accounts.table.capacity'), width: '150px', wrap: true },
  { key: 'weight', label: tr('accounts.weight'), width: '100px' },
  { key: 'quota', label: tr('accounts.table.quota'), width: '230px', wrap: true },
  { key: 'enabled', label: tr('accounts.table.enabled'), width: '100px' },
  { key: 'actions', label: tr('accounts.table.actions'), width: '210px', wrap: true, align: 'right' },
])

onMounted(() => {
  load()
  refresh_timer = setInterval(load, 5000)
})
onUnmounted(() => {
  disposed = true
  clearInterval(refresh_timer)
})

async function load() {
  if (loading.value || disposed) return
  loading.value = true
  try {
    current_load = listNovelDramaAccounts()
    const response = await current_load
    if (disposed) return
    items.value = response.items
    fetched_at.value = new Date().toISOString()
    error.value = ''
  } catch (failure) {
    if (!disposed) error.value = apiErrorLine(failure)
  } finally {
    current_load = null
    if (!disposed) loading.value = false
  }
}

function edit(account) {
  action_error.value = ''
  editing.value = account
  max_sessions.value = String(account.max_sessions)
  weight.value = String(account.weight)
  enabled.value = account.enabled ? 'enabled' : 'disabled'
}

async function update(account, body) {
  if (busy.value) return
  busy.value = account.account_fingerprint
  action_error.value = ''
  try {
    await saveNovelDramaAccount(account.account_fingerprint, body)
    if (disposed) return
    editing.value = null
    toast.success(tr(body.reset_quota ? 'accounts.quotaReset' : 'accounts.saved'))
    if (current_load) await current_load.catch(() => {})
    await load()
  } catch (failure) {
    if (!disposed) action_error.value = apiErrorLine(failure)
  } finally {
    if (!disposed) busy.value = ''
  }
}

async function save() {
  if (!editing.value || max_sessions_invalid.value || weight_invalid.value) return
  await update(editing.value, { enabled: enabled.value === 'enabled', max_sessions: parsed_max_sessions.value, weight: parsed_weight.value })
}
</script>

<template>
  <Section :title="tr('accounts.title')" :padded="false" data-testid="novel-drama-accounts">
    <template #actions>
      <span v-if="fetched_at" class="drama-accounts__updated">{{ tr('accounts.updatedAt', { time: timestamp(fetched_at) }) }}</span>
      <Button size="sm" variant="secondary" :loading="loading" data-testid="novel-drama-accounts-refresh" @click="load">{{ tr('common.refresh') }}</Button>
    </template>
    <p class="drama-accounts__description">{{ tr('accounts.description') }}</p>
    <p class="drama-accounts__weight-hint">{{ tr('accounts.weightHint') }}</p>
    <p v-if="has_uncertain" class="drama-accounts__notice" role="status">{{ tr('accounts.uncertainHint') }}</p>
    <p v-if="error" class="drama-accounts__error" role="alert">{{ error }}</p>
    <p v-if="action_error && !editing" class="drama-accounts__error" role="alert">{{ action_error }}</p>
    <div v-if="loading && !fetched_at" class="drama-accounts__state"><Spinner :label="tr('common.loading')" /><span>{{ tr('common.loading') }}</span></div>
    <div v-else-if="!items.length" class="drama-accounts__state"><EmptyState :title="tr('accounts.empty')" /></div>
    <Table v-else :rows="items" :columns="columns" row-key="account_fingerprint">
      <template #account="{ row }"><div class="drama-accounts__cell"><strong>{{ provider_label(row.provider) }}</strong><span>{{ row.label || tr('common.none') }}</span></div></template>
      <template #phases="{ row }">
        <dl class="drama-accounts__phases">
          <div><dt>{{ tr('accounts.starting') }}</dt><dd>{{ row.starting }}</dd></div>
          <div><dt>{{ tr('accounts.connecting') }}</dt><dd>{{ row.connecting }}</dd></div>
          <div><dt>{{ tr('accounts.performing') }}</dt><dd>{{ row.performing }}</dd></div>
          <div><dt>{{ tr('accounts.uncertain') }}</dt><dd :class="{ 'drama-accounts__warning': row.uncertain > 0 }">{{ row.uncertain ?? tr('common.none') }}</dd></div>
        </dl>
      </template>
      <template #capacity="{ row }"><div class="drama-accounts__cell"><strong :class="{ 'drama-accounts__hot': row.available_slots === 0 }">{{ tr('accounts.occupied', { total: row.total, max: row.max_sessions }) }}</strong><span class="drama-accounts__muted">{{ tr('accounts.availableSlots', { count: row.available_slots }) }}</span></div></template>
      <template #weight="{ row }">{{ row.weight }}</template>
      <template #quota="{ row }"><div class="drama-accounts__cell"><Badge size="sm" :variant="quota_variant(row.quota_status)">{{ quota_label(row.quota_status) }}</Badge><span class="drama-accounts__muted">{{ timestamp(row.quota_checked_at) }}</span><code v-if="row.error_code" class="drama-accounts__hot">{{ row.error_code }}</code></div></template>
      <template #enabled="{ row }"><Badge size="sm" :variant="row.enabled ? 'success' : 'neutral'">{{ tr(row.enabled ? 'accounts.enabled' : 'accounts.disabled') }}</Badge></template>
      <template #actions="{ row }">
        <div class="drama-accounts__actions">
          <Button size="sm" variant="secondary" :disabled="!!busy" :data-testid="`novel-drama-account-edit-${row.account_fingerprint}`" @click="edit(row)">{{ tr('accounts.edit') }}</Button>
          <Button size="sm" variant="ghost" :loading="busy === row.account_fingerprint" :disabled="!!busy" :data-testid="`novel-drama-account-toggle-${row.account_fingerprint}`" @click="update(row, { enabled: !row.enabled })">{{ tr(row.enabled ? 'accounts.disable' : 'accounts.enable') }}</Button>
          <Button v-if="row.quota_status === 'insufficient' || row.quota_status === 'inactive'" size="sm" variant="ghost" :disabled="!!busy" :data-testid="`novel-drama-account-reset-quota-${row.account_fingerprint}`" @click="update(row, { reset_quota: true })">{{ tr('accounts.resetQuota') }}</Button>
        </div>
      </template>
    </Table>
  </Section>
  <Modal :open="!!editing" :title="tr('accounts.editTitle')" width="480px" :close-on-backdrop="!busy" @update:open="!$event && !busy && (editing = null)">
    <form v-if="editing" class="drama-accounts__form" @submit.prevent="save">
      <p>{{ provider_label(editing.provider) }} · {{ editing.label || tr('common.none') }}</p>
      <FormField :label="tr('accounts.maxSessions')" :hint="tr('accounts.maxSessionsHint')" :error="max_sessions_invalid ? tr('accounts.maxSessionsInvalid') : ''">
        <Input v-model="max_sessions" type="number" min="1" max="1000" step="1" :invalid="max_sessions_invalid" :disabled="!!busy" :aria-label="tr('accounts.maxSessions')" data-testid="novel-drama-account-max-sessions" />
      </FormField>
      <FormField :label="tr('accounts.weight')" :hint="tr('accounts.weightHint')" :error="weight_invalid ? tr('accounts.weightInvalid') : ''">
        <Input v-model="weight" type="number" min="1" max="1000" step="1" :invalid="weight_invalid" :disabled="!!busy" :aria-label="tr('accounts.weight')" data-testid="novel-drama-account-weight" />
      </FormField>
      <FormField :label="tr('accounts.table.enabled')"><Select v-model="enabled" :options="enabled_options" :disabled="!!busy" :aria-label="tr('accounts.table.enabled')" block /></FormField>
      <p v-if="action_error" class="drama-accounts__hot" role="alert">{{ action_error }}</p>
    </form>
    <template #footer>
      <Button variant="ghost" :disabled="!!busy" @click="editing = null">{{ tr('common.cancel') }}</Button>
      <Button :loading="!!busy" :disabled="max_sessions_invalid || weight_invalid || !!busy" data-testid="novel-drama-account-save" @click="save">{{ tr('common.save') }}</Button>
    </template>
  </Modal>
</template>

<style scoped>
.drama-accounts__description { margin: 0; padding: var(--drama-space-lg) var(--drama-space-xl); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
.drama-accounts__weight-hint { margin: 0; padding: 0 var(--drama-space-xl) var(--drama-space-lg); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
.drama-accounts__updated { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
.drama-accounts__notice { margin: 0; padding: 0 var(--drama-space-xl) var(--drama-space-lg); color: var(--drama-color-warning); font-size: var(--drama-font-size-sm); }
.drama-accounts__error { margin: 0 var(--drama-space-xl) var(--drama-space-lg); color: var(--drama-color-danger); overflow-wrap: anywhere; }
.drama-accounts__state { display: flex; align-items: center; justify-content: center; gap: var(--drama-space-sm); padding: var(--drama-space-xl); }
.drama-accounts__cell { display: grid; justify-items: start; gap: var(--drama-space-sm); overflow-wrap: anywhere; }
.drama-accounts__muted { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
.drama-accounts__hot { color: var(--drama-color-danger); overflow-wrap: anywhere; }
.drama-accounts__warning { color: var(--drama-color-warning); }
.drama-accounts__phases { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: var(--drama-space-sm); margin: 0; }
.drama-accounts__phases div { display: grid; gap: var(--drama-space-sm); }
.drama-accounts__phases dt { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
.drama-accounts__phases dd { margin: 0; font-variant-numeric: tabular-nums; }
.drama-accounts__actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: var(--drama-space-xs); }
.drama-accounts__form { display: grid; gap: var(--drama-space-md); }
.drama-accounts__form p { margin: 0; }
@media (max-width: 680px) { .drama-accounts__updated { display: none; } }
</style>
